// npm imports
import React, { Fragment, Component } from "react";
import { debounce } from "lodash";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { Input } from "@revvsales/components";

// Components Imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchDropdown from "./SearchDropdown";

// Actions Imports
import {
  updateAssetSearchSuggestion,
  updateAssetSearchContentAction,
} from "../../../../actions/AssetActions/assetActions";
// Internal Imports
import { ASSET } from "../../../../constants";

// Destructuring
const { ASSET_PAGE_URL, ASSET_ITEM_TYPES } = ASSET;

class AssetHeaderSearch extends Component {
  state = {
    searchInput: "",
    showDropdown: false,
    filePointer: -1,
    folderPointer: -1,
  };

  delaySearch = debounce((keyword, options = {}) => {
    this.searchAssetContent(keyword, options);
  }, 400);

  componentDidUpdate(prevProps) {
    // Reset search input on coming back from search page
    if (prevProps.showSearchPage && !this.props.showSearchPage) {
      this._resetSearchTerm();
    }
  }

  _resetSearchTerm = () => {
    this.setState({ searchInput: "" });
  };

  toggleDropdown = show => {
    this.setState({ showDropdown: show });
  };

  _getSearchResults = () => {
    const {
      updateAssetSearchContentAction: updateAssetSearchContent,
      toggleSearchPage,
    } = this.props;
    const { searchInput: searchTerm } = this.state;
    if (searchTerm.trim()) {
      if (searchTerm.length > 2) {
        updateAssetSearchContent(searchTerm)
          .then(() => {
            toggleSearchPage(true);
            this.toggleDropdown(false);
          })
          .catch(err => {
            console.debug(err);
          });
      }
    }
  };

  handleSearchInputChange = e => {
    this.setState({
      searchInput: e.target.value,
    });
    if (e.target.value.length >= 3) {
      this.delaySearch(e.target.value);
    }
  };

  handleKeyDown = e => {
    const { filePointer, folderPointer } = this.state;
    const { assetSearchSuggestions } = this.props;
    let fileSuggestions = [];
    if (
      assetSearchSuggestions &&
      assetSearchSuggestions.files &&
      assetSearchSuggestions.files.length
    ) {
      fileSuggestions = assetSearchSuggestions.files;
    }

    let folderSuggestions = [];
    if (
      assetSearchSuggestions &&
      assetSearchSuggestions.folders &&
      assetSearchSuggestions.folders.length
    ) {
      folderSuggestions = assetSearchSuggestions.folders;
    }
    if (e.key === "Enter") {
      if (filePointer !== -1 && filePointer !== null) {
        this._redirectionHandling(fileSuggestions[filePointer]);
      } else if (folderPointer !== -1) {
        this._redirectionHandling(folderSuggestions[folderPointer]);
      } else {
        this._getSearchResults();
      }
    }

    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38) {
      if (fileSuggestions.length > 0 && folderSuggestions.length === 0) {
        if (filePointer > 0) {
          this.setState(prevState => ({
            filePointer: prevState.filePointer - 1,
          }));
        }
      }
      if (folderSuggestions.length > 0 && fileSuggestions.length === 0) {
        if (folderPointer > 0) {
          this.setState(prevState => ({
            folderPointer: prevState.folderPointer - 1,
          }));
        }
      }
      if (folderSuggestions.length > 0 && fileSuggestions.length > 0) {
        if (folderPointer > 0) {
          this.setState(prevState => ({
            folderPointer: prevState.folderPointer - 1,
          }));
        } else if (filePointer === null) {
          this.setState({
            filePointer: fileSuggestions.length - 1,
            folderPointer: -1,
          });
        } else if (filePointer > 0) {
          this.setState(prevState => ({
            filePointer: prevState.filePointer - 1,
          }));
        }
      }
    } else if (e.keyCode === 40) {
      if (fileSuggestions.length > 0 && folderSuggestions.length === 0) {
        if (filePointer < fileSuggestions.length - 1) {
          this.setState(prevState => ({
            filePointer: prevState.filePointer + 1,
          }));
        }
      }
      if (folderSuggestions.length > 0 && fileSuggestions.length === 0) {
        if (folderPointer < folderSuggestions.length - 1) {
          this.setState(prevState => ({
            folderPointer: prevState.folderPointer + 1,
          }));
        }
      }
      if (folderSuggestions.length > 0 && fileSuggestions.length > 0) {
        if (filePointer !== null && filePointer < fileSuggestions.length - 1) {
          this.setState(prevState => ({
            filePointer: prevState.filePointer + 1,
          }));
        } else if (folderPointer < folderSuggestions.length - 1) {
          this.setState(prevState => ({
            folderPointer: prevState.folderPointer + 1,
            filePointer: null,
          }));
        }
      }
    }
  };

  _redirectionHandling = item => {
    const route = this._getAssetFolderUrl(
      item.doc_no,
      item.id,
      item.name,
      item.type,
      item.opportunity_id,
      item.opportunity_root,
    );
    setTimeout(() => this.props.history.push(route), 0);
    this.setState({
      filePointer: -1,
      folderPointer: -1,
    });
    this.toggleDropdown(false);
  };

  _getAssetFolderUrl = (
    docNo,
    folderId,
    folderName,
    folderType,
    opportunityId,
    opportunityRoot,
  ) => {
    // Check, If the current item is a "Folder" or Not
    let isFolder = false;
    if (folderType === ASSET_ITEM_TYPES.FOLDER) {
      isFolder = true;
    }
    let url = "";
    if (isFolder) {
      url = `/${ASSET_PAGE_URL}/${folderName
        .toLocaleLowerCase()
        .split(" ")
        .join("-")}/${folderId}`;

      if (opportunityRoot && opportunityId) {
        url = `${url}/?opp_id=${opportunityId}`;
      }
    } else {
      url = `/documents/${docNo}`;
    }
    return url;
  };

  searchAssetContent = searchTerm => {
    const { updateAssetSearchSuggestion: updateSuggestion } = this.props;
    updateSuggestion(searchTerm).catch(() => {});
  };

  render() {
    const {
      searchInput,
      showDropdown,
      filePointer,
      folderPointer,
    } = this.state;
    const { /* showSearchPage, */ toggleSearchPage } = this.props;

    return (
      <Fragment>
        <div className="assetheader-search">
          <FontAwesomeIcon
            icon={["far", "search"]}
            className="assetheader-searchicon"
          />
          <Input
            data-test="document-search-input"
            className="assetheader-input"
            placeholder="Search documents, folders or people"
            value={searchInput}
            onFocus={() => this.toggleDropdown(true)}
            onChange={this.handleSearchInputChange}
            onKeyDown={this.handleKeyDown}
            data-id="asset-search"
          />
          {/* {showSearchPage ? (
            <span
              className="assetheader-closeicon-parent"
              onKeyDown={() => {}}
              tabIndex="0"
              role="button"
              onClick={() => toggleSearchPage(false)}>
              <FontAwesomeIcon
                icon={["far", "times-circle"]}
                className="assetheader-closeicon"
              />
            </span>
          ) : null} */}
          <SearchDropdown
            showDropdown={showDropdown}
            filePointer={filePointer}
            folderPointer={folderPointer}
            toggleDropdown={this.toggleDropdown}
            searchTerm={searchInput}
            toggleSearchPage={toggleSearchPage}
          />
        </div>
      </Fragment>
    );
  }
}

AssetHeaderSearch.propTypes = {
  toggleSearchPage: PropTypes.func.isRequired,
  updateAssetSearchSuggestion: PropTypes.func.isRequired,
  showSearchPage: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ AssetStore: { assetSearchSuggestions = {} } }) => ({
  assetSearchSuggestions,
});

const mapDispatchToProps = {
  updateAssetSearchSuggestion,
  updateAssetSearchContentAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(AssetHeaderSearch));



// WEBPACK FOOTER //
// ./src/components/AssetWrapper/AssetHeader/AssetHeaderSearch/index.js